import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useAuthStore } from './store/authStore'
import { track } from './lib/tracker'
import { capturePageview } from './lib/analytics'
import CookieConsent from './components/CookieConsent'

function RouteTracker() {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  useEffect(() => {
    track('page_view')
    // Tag the pageview so acquisition (website) and product usage (portal) stay
    // separable in PostHog even though they share a domain.
    capturePageview(isAuthenticated ? 'app' : 'marketing')
  }, [location.pathname, isAuthenticated])
  return null
}
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import GitHubCallbackPage from './pages/GitHubCallbackPage'
import SetPasswordPage from './pages/SetPasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import PricingPage from './pages/PricingPage'
import DashboardPage from './pages/DashboardPage'
import CreateInstancePage from './pages/CreateInstancePage'
import InstanceDetailPage from './pages/InstanceDetailPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import BillingPage from './pages/BillingPage'
import ProfilePage from './pages/ProfilePage'
import UsersPage from './pages/UsersPage'
import AdminMailQueuePage from './pages/AdminMailQueuePage'
import AdminApprovalsPage from './pages/AdminApprovalsPage'
import AdminMailPage from './pages/AdminMailPage'
import AnalyticsPage from './pages/AnalyticsPage'
import InfrastructurePage from './pages/InfrastructurePage'
import BusinessPage from './pages/BusinessPage'
import TemplatesPage from './pages/TemplatesPage'
import PlatformSettingsPage from './pages/PlatformSettingsPage'
import TrafficLogsPage from './pages/TrafficLogsPage'
import ModulesPage from './pages/ModulesPage'
import AdminImagesPage from './pages/AdminImagesPage'
import MarketplacePage from './pages/MarketplacePage'
import MarketplaceAppPage from './pages/MarketplaceAppPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import KunyePage from './pages/KunyePage'
import AboutPage from './pages/AboutPage'
import DistanceSalesPage from './pages/DistanceSalesPage'
import DeliveryReturnPage from './pages/DeliveryReturnPage'
import ProductsPage from './pages/ProductsPage'
import ProductDetailPage from './pages/ProductDetailPage'
import CartPage from './pages/CartPage'
import DocsPage from './pages/DocsPage'
import Layout from './components/Layout'
import ProfileGate from './components/ProfileGate'
import RequireAdmin from './components/RequireAdmin'
import { isSelfHosted, mailEnabled } from './config/edition'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
          <div className="bg-white border border-red-200 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold text-red-700 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-600 mb-4">
              The app hit an unexpected error. Try again or reload the page.
            </p>
            <div className="flex gap-2">
              <button
                onClick={resetError}
                className="px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      )}
    >
    <>
    <RouteTracker />
    {/* Cookie consent gate (SaaS only — the self-hosted partner build ships no
        third-party trackers, so it needs no banner). Self-gates internally. */}
    {!isSelfHosted && <CookieConsent />}
    <Routes>
      {/* Self-hosted has no marketing landing — root goes straight to login. */}
      <Route path="/" element={isSelfHosted ? <Navigate to="/login" replace /> : <LandingPage />} />

      {/* Public auth & onboarding */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/docs" element={<DocsPage />} />
      {/* Legal pages: the path itself carries the language. Turkish slugs → TR, English slugs → EN. */}
      <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage lang="tr" />} />
      <Route path="/hizmet-sartlari" element={<TermsOfServicePage lang="tr" />} />
      <Route path="/privacy" element={<PrivacyPolicyPage lang="en" />} />
      <Route path="/terms" element={<TermsOfServicePage lang="en" />} />
      <Route path="/kunye" element={<KunyePage lang="tr" />} />
      <Route path="/legal-notice" element={<KunyePage lang="en" />} />
      <Route path="/hakkimizda" element={<AboutPage lang="tr" />} />
      <Route path="/about" element={<AboutPage lang="en" />} />
      <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesPage lang="tr" />} />
      <Route path="/distance-sales-agreement" element={<DistanceSalesPage lang="en" />} />
      <Route path="/teslimat-ve-iade" element={<DeliveryReturnPage lang="tr" />} />
      <Route path="/delivery-and-returns" element={<DeliveryReturnPage lang="en" />} />

      {/* SaaS-only public routes: self-signup, GitHub sign-in, pricing, storefront. */}
      {!isSelfHosted && (
        <>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/github/callback" element={<GitHubCallbackPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* Storefront: catalogue → product detail → cart → payment. */}
          <Route path="/urunler" element={<ProductsPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/urunler/:slug" element={<ProductDetailPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
          <Route path="/sepet" element={<CartPage />} />
          <Route path="/cart" element={<CartPage />} />
        </>
      )}

      {/* Protected app — pathless layout wrapper */}
      <Route
        element={
          <RequireAuth>
            {/* ProfileGate (SaaS): holds the app until the account has the
                company name + phone the BTK domain report needs. GitHub
                signups land here; everyone else sails through. */}
            <ProfileGate>
              <Layout />
            </ProfileGate>
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/instances/new" element={<CreateInstancePage />} />
        <Route path="/apps" element={<MarketplacePage />} />
        <Route path="/apps/:slug" element={<MarketplaceAppPage />} />
        <Route path="/instances/:id/*" element={<InstanceDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings/password" element={<ChangePasswordPage />} />
        {/* Admin-only routes behind the server-verified role guard: a
            non-admin (or a tampered localStorage role) is redirected before
            the page ever mounts. Backend endpoints enforce RequireAdmin
            regardless. */}
        <Route element={<RequireAdmin />}>
          <Route path="/users" element={<UsersPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/platform-settings" element={<PlatformSettingsPage />} />
          <Route path="/admin/traffic-logs" element={<TrafficLogsPage />} />
          <Route path="/modules" element={<ModulesPage />} />
          <Route path="/admin/images" element={<AdminImagesPage />} />
          <Route path="/infrastructure" element={<InfrastructurePage />} />
          <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
          {!isSelfHosted && (
            <>
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/business" element={<BusinessPage />} />
            </>
          )}
          {mailEnabled && (
            <>
              <Route path="/admin/mail" element={<AdminMailPage />} />
              <Route path="/admin/mail-queue" element={<AdminMailQueuePage />} />
            </>
          )}
        </Route>

        {/* SaaS-only user-level pages. */}
        {!isSelfHosted && (
          <>
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/usage-revenue" element={<Navigate to="/business" replace />} />
          </>
        )}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
    </Sentry.ErrorBoundary>
  )
}
